import i18n from "i18n";

/** `t` from useTranslation, typed loosely as the other error helpers do. */
type TFunction = (key: string, opts?: any) => string;

/**
 * Error codes the external-contract endpoints answer with, and the message key
 * for each — from `services/orders/attach-external-document.js`
 * (`ATTACH_ERROR_STATUS`) and `services/orders/external-contract.js`
 * (`EXTERNAL_CONTRACT_ERROR_CODES`, used by `orders/patch` and
 * `files/build-pdf`'s amendment-slot check). A code missing here, or no code
 * at all, falls back to the HTTP status below rather than the server's raw
 * English text.
 */
const CODE_KEYS: Record<string, string> = {
  // orders/patch (services/orders/external-contract.js)
  EXTERNAL_NUMBER_REQUIRED: "order_external_number_required",
  INVALID_EXTERNAL_NUMBER: "order_external_number_too_long",
  INVALID_ORIGIN: "order_external_invalid_origin",
  ORIGIN_LOCKED: "order_external_origin_locked",
  BILLING_START_BEFORE_IMPORT: "billing_start_before_import",
  SIGNED_ORDER_EDIT_FORBIDDEN: "signed_order_edit_forbidden",
  // orders/patch, orders/post, orders/invoice-settings
  // (services/orders/invoice-settings.js)
  INVALID_CONTRACT_END: "order_invalid_contract_end",
  // orders/attach-document (services/orders/attach-external-document.js)
  ORDER_NOT_FOUND: "external_document_order_not_found",
  FILE_NOT_FOUND: "file_not_found",
  NOT_PDF: "external_document_not_pdf",
  DOCUMENT_ALREADY_SENT: "external_document_already_sent",
  ATTACH_FORBIDDEN: "external_document_forbidden",
  FILE_ALREADY_ATTACHED: "external_document_file_attached",
  ORDER_NOT_EXTERNAL: "external_document_order_not_external",
  DOCUMENT_ANNULLED: "external_document_annulled",
  INVALID_FILE_TYPE: "external_document_invalid_file_type",
  INVALID_CATEGORY: "external_document_invalid_category",
  INVALID_DATE: "external_document_invalid_date",
  // files/build-pdf, amendment of an external contract's document
  AMENDMENT_SLOT_REQUIRED: "amendment_slot_required",
};

/**
 * The user-facing message for a failed request to an order or external-
 * contract endpoint. Not only the external-contract ones above — this is
 * also what `order-form.tsx` uses for every order save, so a code outside
 * `CODE_KEYS` (an invoice-settings refusal — schedule day, billing start,
 * "order is closed", ... — `services/orders/invoice-settings.js`,
 * `services/orders/close-order.js`) must still read as something specific,
 * not a generic fallback.
 */
export function externalContractErrorMessage(error: any, t: TFunction): string {
  const data = error?.response?.data;
  const code = data?.code || data?.error;
  if (code && CODE_KEYS[code]) {
    return t(CODE_KEYS[code], {
      month: data?.minBillingStartDate ? String(data.minBillingStartDate).slice(0, 7) : "",
    });
  }
  // Not one of the codes above. Most order refusals reach here: the server
  // sends its own `message` and no code the table above knows about. Some of
  // those messages are themselves i18n keys (the convention
  // reset-file-signatures-confirm.tsx's `t(errMsg)` relies on — snake_case
  // strings like "access_denied"); most, from orders/patch's invoice-settings
  // and close-order guards, are already human-readable English sentences
  // meant to be shown as written. `i18n.exists` tells the two apart, so
  // either way the text is used only once — never handed to `t()` a second
  // time (a message containing ":" would otherwise be misread as an i18next
  // namespace separator; see order-form.tsx's errMsg banner).
  const message = data?.message;
  if (message) {
    // `as any`: src/i18n.js re-exports the i18next instance under a name
    // that collides with baseUrl-relative resolution (see billing-period.ts,
    // same cast for the same reason) — its inferred type is unreliable here.
    return (i18n as any).exists(message) ? t(message) : message;
  }
  // No server message at all (a transport failure, or a policy refusal with
  // nothing else) — fall back on the HTTP status, so the user still reads a
  // translated sentence instead of a raw Axios message ("Request failed with
  // status code 403").
  const status = error?.response?.status;
  if (status === 403) return t("forbidden");
  if (status === 404) return t("external_contract_not_found");
  return t("unexpected_error");
}
